import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Logo } from './Logo';

interface CustomerLayoutProps {
  children: React.ReactNode;
}

export const CustomerLayout: React.FC<CustomerLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="layout">
      <header className="header">
        <div className="header-container">
          <div className="header-left">
            <Link to="/customer/dashboard" className="logo-link">
              <Logo size="md" variant="full" />
            </Link>

            {/* Mobile Menu Button */}
            <button
              className="mobile-menu-btn"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? '✕' : '☰'}
            </button>
          </div>

          <nav className={`nav ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
            <Link to="/customer/dashboard" className="nav-link" onClick={closeMobileMenu}>Dashboard</Link>
            <Link to="/customer/applications" className="nav-link" onClick={closeMobileMenu}>Applications</Link>
            <Link to="/customer/loans" className="nav-link" onClick={closeMobileMenu}>Loans</Link>
            <Link to="/customer/documents" className="nav-link" onClick={closeMobileMenu}>Documents</Link>
            <Link to="/customer/support" className="nav-link" onClick={closeMobileMenu}>Support</Link>
            <Link to="/customer/help" className="nav-link" onClick={closeMobileMenu}>Help</Link>

            {/* Settings Dropdown */}
            <div className="nav-dropdown">
              <button
                className="nav-link dropdown-toggle"
                onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                onBlur={() => setTimeout(() => setShowSettingsDropdown(false), 200)}
              >
                Settings ▼
              </button>
              {showSettingsDropdown && (
                <div className="dropdown-menu">
                  <Link to="/customer/settings/profile" className="dropdown-item" onClick={closeMobileMenu}>Profile</Link>
                  <Link to="/customer/settings/notifications" className="dropdown-item" onClick={closeMobileMenu}>Notifications</Link>
                </div>
              )}
            </div>

            <Link to="/customer/applications/new" className="nav-link primary-nav-link" onClick={closeMobileMenu}>Apply for Loan</Link>

            {/* Mobile User Info */}
            <div className="mobile-user-info">
              <span className="user-name">
                {user?.first_name} {user?.last_name}
              </span>
              <button onClick={() => { handleLogout(); closeMobileMenu(); }} className="btn btn-secondary btn-sm">
                Sign Out
              </button>
            </div>
          </nav>

          <div className="header-actions">
            <span className="user-name">
              {user?.first_name} {user?.last_name}
            </span>
            <button onClick={handleLogout} className="btn btn-secondary btn-sm">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={closeMobileMenu} />
      )}

      <main className="main-content">
        {children}
      </main>

      <footer className="footer">
        <div className="footer-container">
          <p>&copy; 2025 Coin MFI. Fast, secure microfinance loans. All rights reserved.</p>
        </div>
      </footer>

      <style>{`
        /* Header Left Container */
        .header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        /* Mobile Menu Button - Hidden on desktop */
        .mobile-menu-btn {
          display: none;
          background: none;
          border: none;
          font-size: 28px;
          cursor: pointer;
          padding: 8px;
          color: #1a1a1a;
          z-index: 1001;
          line-height: 1;
        }

        /* Mobile Menu Overlay */
        .mobile-menu-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 999;
        }

        /* Mobile User Info - Hidden on desktop */
        .mobile-user-info {
          display: none;
        }

        .nav-dropdown {
          position: relative;
          display: inline-block;
        }

        .dropdown-toggle {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          background: white;
          border: 1px solid #e5e5e5;
          border-radius: 6px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          min-width: 180px;
          margin-top: 4px;
          z-index: 1000;
        }

        .dropdown-item {
          display: block;
          padding: 10px 16px;
          color: #1a1a1a;
          text-decoration: none;
          font-size: 14px;
          transition: background-color 0.2s;
        }

        .dropdown-item:hover {
          background-color: #f5f5f5;
        }

        .dropdown-item:first-child {
          border-top-left-radius: 6px;
          border-top-right-radius: 6px;
        }

        .dropdown-item:last-child {
          border-bottom-left-radius: 6px;
          border-bottom-right-radius: 6px;
        }

        .primary-nav-link {
          background-color: #4f46e5;
          color: white !important;
          padding: 8px 16px;
          border-radius: 6px;
          transition: background-color 0.2s;
        }

        .primary-nav-link:hover {
          background-color: #4338ca;
        }

        /* Tablet - Add horizontal scroll to nav */
        @media (max-width: 1024px) {
          .header-container {
            overflow-x: auto;
            overflow-y: hidden;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: thin;
            scrollbar-color: #cbd5e0 #f7fafc;
          }

          .header-container::-webkit-scrollbar {
            height: 6px;
          }

          .header-container::-webkit-scrollbar-track {
            background: #f7fafc;
            border-radius: 3px;
          }

          .header-container::-webkit-scrollbar-thumb {
            background: #cbd5e0;
            border-radius: 3px;
          }

          .header-container::-webkit-scrollbar-thumb:hover {
            background: #a0aec0;
          }

          .nav {
            flex-shrink: 0;
            white-space: nowrap;
          }

          .nav-link {
            font-size: 14px;
            padding: 6px 12px;
          }

          .primary-nav-link {
            padding: 6px 12px;
            font-size: 14px;
          }

          .header-actions {
            flex-shrink: 0;
            white-space: nowrap;
          }

          .header-actions .user-name {
            font-size: 14px;
          }
        }

        /* Mobile - Switch to hamburger menu */
        @media (max-width: 768px) {
          .header-container {
            overflow: visible;
          }

          .mobile-menu-btn {
            display: block;
          }

          .mobile-menu-overlay {
            display: block;
          }

          .header-actions {
            display: none;
          }

          .nav {
            position: fixed;
            top: 0;
            right: -100%;
            height: 100vh;
            width: 280px;
            max-width: 80%;
            background: white;
            flex-direction: column;
            align-items: stretch;
            padding: 80px 0 20px 0;
            box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
            transition: right 0.3s ease-in-out;
            z-index: 1000;
            overflow-y: auto;
          }

          .nav.mobile-open {
            right: 0;
          }

          .nav-link {
            padding: 14px 24px;
            border-bottom: 1px solid #f0f0f0;
            text-align: left;
            display: block;
            width: 100%;
            white-space: normal;
          }

          .nav-dropdown {
            display: block;
            width: 100%;
          }

          .dropdown-toggle {
            width: 100%;
            text-align: left;
            padding: 14px 24px;
            border-bottom: 1px solid #f0f0f0;
          }

          .dropdown-menu {
            position: static;
            box-shadow: none;
            border: none;
            border-radius: 0;
            margin: 0;
            background: #f9f9f9;
          }

          .dropdown-item {
            padding: 12px 24px 12px 40px;
            border-bottom: 1px solid #f0f0f0;
          }

          .primary-nav-link {
            margin: 12px 24px;
            width: calc(100% - 48px);
            text-align: center;
          }

          .mobile-user-info {
            display: flex;
            flex-direction: column;
            gap: 12px;
            padding: 20px 24px;
            border-top: 2px solid #e5e5e5;
            margin-top: auto;
          }

          .mobile-user-info .user-name {
            font-weight: 600;
            color: #1a1a1a;
          }

          .mobile-user-info .btn {
            width: 100%;
          }

          .logo-link img {
            max-width: 120px;
          }
        }

        /* Small Mobile */
        @media (max-width: 480px) {
          .header-container {
            padding: 0 12px;
          }

          .nav {
            width: 100%;
            max-width: 100%;
          }

          .logo-link img {
            max-width: 100px;
          }

          .mobile-menu-btn {
            font-size: 24px;
            padding: 6px;
          }
        }
      `}</style>
    </div>
  );
};
